import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { getStateFromLatLng } from "../../../utils/getStateFromLatLng"; // Utility for reverse geocoding
import {
  getStateBoundaryCoords,
  getAllStateNames,
} from "../../../utils/stateBoundaries"; // Utility for state boundary highlighting
import LocationPinGolfSelected from "../../../assets/offers/LocationPin-Golf-Selected.svg";
import LocationPinGolfUnselected from "../../../assets/offers/LocationPin-Golf-Unselected.svg";
import LocationPinSkiSelected from "../../../assets/offers/LocationPin-Ski-Selected.svg";
import LocationPinSkiUnselected from "../../../assets/offers/LocationPin-Ski-Unselected.svg";
import LocationPinBeachSelected from "../../../assets/offers/LocationPin-Beach-Selected.svg";
import LocationPinBeachUnselected from "../../../assets/offers/LocationPin-Beach-Unselected.svg";
import LocationPinBlack from "../../../assets/offers/LocationPin-Black.svg";
import { useGoogleMapsKey } from "../../../hooks/useGoogleMapsKey";
import { loadGoogleMaps } from "../../../utils/loadGoogleMaps";
import { useSearchStore } from "../../../hooks/useSearchStore";
import "./GoogleMap.css";

const GoogleMap = forwardRef(
  (
    {
      hotels = [],
      className = "",
      passion = "golf",
      offersContentRef,
      heroSectionRef,
      onMarkerClick,
      onSearchByLocation, // callback for search action
    },
    ref,
  ) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const markerClustererRef = useRef(null);
    const containerRef = useRef(null);
    const clickedMarkerRef = useRef(null);
    const statePolygonRef = useRef(null); // For highlighting state boundaries
    const clickableStatePolygonsRef = useRef([]); // For clickable state polygons
    const [isLoaded, setIsLoaded] = useState(false);
    const [isApiReady, setIsApiReady] = useState(false);
    const [isSticky, setIsSticky] = useState(false);

    // State for search by location button
    const [showSearchBtn, setShowSearchBtn] = useState(false);
    const [searchState, setSearchState] = useState("");
    const [searchCenter, setSearchCenter] = useState(null);
    const [hoveredState, setHoveredState] = useState(""); // Track hovered state
    const [selectedState, setSelectedState] = useState("");
    const selectedStateRef = useRef("");

    const ZOOM_LEVEL = 5;

    useEffect(() => {
      selectedStateRef.current = selectedState;
    }, [selectedState]);

    // Function to highlight a state on the map
    const highlightState = (stateName) => {
      if (!mapInstanceRef.current || !stateName) return;

      // Remove existing state polygon
      if (statePolygonRef.current) {
        statePolygonRef.current.setMap(null);
        statePolygonRef.current = null;
      }

      // Get state boundary coordinates
      const boundaryCoords = getStateBoundaryCoords(stateName);
      if (!boundaryCoords) return;

      // Create polygon overlay
      const statePolygon = new window.google.maps.Polygon({
        paths: boundaryCoords,
        strokeColor: "#FF6B35", // Orange border
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF6B35", // Orange fill
        fillOpacity: 0.1,
        map: mapInstanceRef.current,
      });

      statePolygonRef.current = statePolygon;
    };

    // Function to clear state highlighting
    const clearStateHighlight = () => {
      if (statePolygonRef.current) {
        statePolygonRef.current.setMap(null);
        statePolygonRef.current = null;
      }

      // Clear search button and state
      setShowSearchBtn(false);
      setSearchState("");
      setSearchCenter(null);
      if (selectedStateRef.current) {
        setSelectedState("");
      }
      selectedStateRef.current = "";

      // Reset all clickable polygons to transparent
      clickableStatePolygonsRef.current.forEach((polygon) => {
        polygon.setOptions({
          fillColor: "transparent",
          fillOpacity: 0,
          strokeColor: "transparent",
          strokeOpacity: 0,
          strokeWeight: 0,
        });
      });
    };

    // Function to handle state polygon click
    const handleStateClick = async (stateName) => {
      // Clear any existing hover effects on all clickable polygons
      clickableStatePolygonsRef.current.forEach((polygon) => {
        polygon.setOptions({
          fillColor: "transparent",
          fillOpacity: 0,
          strokeColor: "transparent",
          strokeOpacity: 0,
          strokeWeight: 0,
        });
      });

      // Get the center of the clicked state
      const bounds = new window.google.maps.LatLngBounds();
      const boundaryCoords = getStateBoundaryCoords(stateName);

      if (boundaryCoords) {
        boundaryCoords.forEach((coord) => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });

        const center = bounds.getCenter();
        const newCenter = { lat: center.lat(), lng: center.lng() };
        setSearchCenter(newCenter);
        setSearchState(stateName);
        setSelectedState(stateName);
        selectedStateRef.current = stateName;
        setShowSearchBtn(true);

        // Highlight the clicked state
        highlightState(stateName);
      }
    };

    // Function to create clickable state polygons
    const createClickableStatePolygons = () => {
      if (!mapInstanceRef.current) return;

      // Clear existing clickable polygons
      clickableStatePolygonsRef.current.forEach((polygon) => {
        polygon.setMap(null);
      });
      clickableStatePolygonsRef.current = [];

      // Create clickable polygons for all states
      const stateNames = getAllStateNames();

      stateNames.forEach((stateName) => {
        const boundaryCoords = getStateBoundaryCoords(stateName);
        if (!boundaryCoords) return;

        // Create invisible clickable polygon
        const clickablePolygon = new window.google.maps.Polygon({
          paths: boundaryCoords,
          strokeColor: "transparent",
          strokeOpacity: 0,
          strokeWeight: 0,
          fillColor: "transparent",
          fillOpacity: 0,
          map: mapInstanceRef.current,
          clickable: true,
        });

        // Add click listener
        clickablePolygon.addListener("click", (event) => {
          // Stop event propagation to prevent map click from clearing highlight
          event.stop();
          handleStateClick(stateName);
        });

        // Add hover effects
        clickablePolygon.addListener("mouseover", () => {
          clickablePolygon.setOptions({
            fillColor: "#FF6B35",
            fillOpacity: 0.1,
            strokeColor: "#FF6B35",
            strokeOpacity: 0.3,
            strokeWeight: 1,
          });
          // Update hovered state to show in button
          setHoveredState(stateName);

          // Update search center for the hovered state
          const bounds = new window.google.maps.LatLngBounds();
          const hoveredBoundaryCoords = getStateBoundaryCoords(stateName);
          if (hoveredBoundaryCoords) {
            hoveredBoundaryCoords.forEach((coord) => {
              bounds.extend(
                new window.google.maps.LatLng(coord.lat, coord.lng),
              );
            });
            const center = bounds.getCenter();
            setSearchCenter({ lat: center.lat(), lng: center.lng() });
          }
        });

        clickablePolygon.addListener("mouseout", () => {
          // Only clear hover if this isn't the currently selected state
          if (selectedStateRef.current !== stateName) {
            clickablePolygon.setOptions({
              fillColor: "transparent",
              fillOpacity: 0,
              strokeColor: "transparent",
              strokeOpacity: 0,
              strokeWeight: 0,
            });
          }
          // Clear hovered state and restore to selected state's center
          setHoveredState("");

          // Restore search center to selected state if there is one
          if (selectedStateRef.current) {
            const bounds = new window.google.maps.LatLngBounds();
            const selectedBoundaryCoords = getStateBoundaryCoords(
              selectedStateRef.current,
            );
            if (selectedBoundaryCoords) {
              selectedBoundaryCoords.forEach((coord) => {
                bounds.extend(
                  new window.google.maps.LatLng(coord.lat, coord.lng),
                );
              });
              const center = bounds.getCenter();
              setSearchCenter({ lat: center.lat(), lng: center.lng() });
            }
          }
        });

        clickableStatePolygonsRef.current.push(clickablePolygon);
      });
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      centerOnHotel: (hotel) => {
        if (!mapInstanceRef.current || !hotel.latitude || !hotel.longitude)
          return;

        const position = {
          lat: parseFloat(hotel.latitude),
          lng: parseFloat(hotel.longitude),
        };

        // Find the corresponding marker by hotel ID (more reliable than name)
        const marker = markersRef.current.find(
          (marker) => marker.hotelId === hotel.id,
        );

        if (marker) {
          // Center the map on the marker position
          mapInstanceRef.current.panTo(position);

          // Zoom in to focus on the selected region
          mapInstanceRef.current.setZoom(12);

          // Set this marker as clicked
          clickedMarkerRef.current = marker;

          // Close any existing InfoWindow first
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          // Create and show InfoWindow at the correct marker position
          const createInfoWindowContent = (hotel) => {
            const rating = hotel.reviews?.rating
              ? parseFloat(hotel.reviews.rating).toFixed(1)
              : "N/A";
            const reviewCount = hotel.reviews?.count || 0;
            const priceValue =
              hotel.rate?.lowestAverageNightlyRate?.value ||
              hotel.rate?.firstNightLowestStay?.value;
            const price = priceValue
              ? `$${(priceValue / Math.pow(10, hotel.rate.firstNightLowestStay?.valueDecimalPoint || 2)).toFixed(0)}`
              : "Price not available";
            const description = hotel.description || "No description available";

            return `
            <div class="custom-info-window">
              <div class="info-window-header">
                <h3 class="hotel-name">${hotel.name}</h3>
                <div class="hotel-rating">
                  <span class="rating-stars">★</span>
                  <span class="rating-value">${rating}</span>
                  <span class="review-count">(${reviewCount} reviews)</span>
                </div>
              </div>
              <div class="info-window-content">
                <p class="hotel-description">${description.length > 100 ? description.substring(0, 100) + "..." : description}</p>
                <div class="hotel-price">
                  <span class="price-label">From </span>
                  <span class="price-value">${price}</span>
                  <span class="price-period"> per night</span>
                </div>
              </div>
            </div>
          `;
          };

          // Show InfoWindow at the marker's position
          infoWindowRef.current.setContent(createInfoWindowContent(hotel));
          infoWindowRef.current.open(mapInstanceRef.current, marker);
        } else {
          console.warn(
            `Marker not found for hotel: ${hotel.name} (ID: ${hotel.id})`,
          );
        }
      },
    }));

    const iconMap = {
      golf: {
        selected: LocationPinGolfSelected,
        unselected: LocationPinGolfUnselected,
      },
      ski: {
        selected: LocationPinSkiSelected,
        unselected: LocationPinSkiUnselected,
      },
      beach: {
        selected: LocationPinBeachSelected,
        unselected: LocationPinBeachUnselected,
      },
    };

    const { apiKey, loading: keyLoading, error: keyError } = useGoogleMapsKey();

    // Load Google Maps API script dynamically once we have the key
    useEffect(() => {
      if (keyLoading || keyError || !apiKey) return;

      loadGoogleMaps(apiKey)
        .then(() => {
          setIsApiReady(true);
        })
        .catch((error) => {
          console.error("Error loading Google Maps script:", error);
        });
    }, [apiKey, keyLoading, keyError]);

    // Initialize Google Maps once API is ready
    useEffect(() => {
      if (!isApiReady || !mapRef.current) return;

      const initializeMap = () => {
        try {
          // Default center (can be adjusted based on hotels)
          const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // Center of US

          // Create map
          const map = new window.google.maps.Map(mapRef.current, {
            zoom: 4,
            center: defaultCenter,
            // Disable all controls except zoom
            mapTypeControl: false, // Removes map/satellite toggle
            streetViewControl: false, // Removes street view control
            fullscreenControl: false, // Removes fullscreen button
            zoomControl: true, // Keep zoom in/out buttons
            gestureHandling: "cooperative", // Requires ctrl+scroll to zoom
          });

          mapInstanceRef.current = map;

          // Add click listener to map to close InfoWindow when clicking elsewhere
          map.addListener("click", () => {
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }
            clickedMarkerRef.current = null;

            // Clear temporary highlights only when nothing is selected
            if (!selectedStateRef.current) {
              clearStateHighlight();
            } else {
              highlightState(selectedStateRef.current);
            }
          });

          // Add listeners for dragend and zoom_changed
          map.addListener("dragend", async () => {
            if (selectedStateRef.current) {
              return;
            }

            const center = map.getCenter();
            const zoom = map.getZoom();
            if (zoom > ZOOM_LEVEL) {
              const newCenter = { lat: center.lat(), lng: center.lng() };
              setSearchCenter(newCenter);
              const state = await getStateFromLatLng(
                center.lat(),
                center.lng(),
              );
              setSearchState(state);
              setShowSearchBtn(true);
              highlightState(state); // Highlight the detected state
            } else {
              setShowSearchBtn(false);
              clearStateHighlight(); // Clear highlighting when zoomed out
            }
          });
          map.addListener("zoom_changed", async () => {
            if (selectedStateRef.current) {
              return;
            }

            const center = map.getCenter();
            const zoom = map.getZoom();
            if (zoom > ZOOM_LEVEL) {
              const newCenter = { lat: center.lat(), lng: center.lng() };
              setSearchCenter(newCenter);
              const state = await getStateFromLatLng(
                center.lat(),
                center.lng(),
              );
              setSearchState(state);
              setShowSearchBtn(true);
              highlightState(state); // Highlight the detected state
            } else {
              setShowSearchBtn(false);
              clearStateHighlight(); // Clear highlighting when zoomed out
            }
          });

          // Create clickable state polygons
          createClickableStatePolygons();

          setIsLoaded(true);

          console.log("Google Map initialized successfully");
        } catch (error) {
          console.error("Error initializing Google Map:", error);
        }
      };

      initializeMap();
    }, [isApiReady]);

    // Scroll event listener to set sticky state
    useEffect(() => {
      const handleScroll = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();

          let shouldBeSticky = false;

          const heroSection = heroSectionRef?.current;
          const offersContent = offersContentRef?.current;

          if (heroSection && offersContent) {
            const heroRect = heroSection.getBoundingClientRect();
            const offersRect = offersContent.getBoundingClientRect();

            // Map should be sticky when:
            // 1. Hero section is completely scrolled past (bottom is above viewport) AND
            // 2. Offers content is at or above the top of viewport
            shouldBeSticky = heroRect.bottom <= 0 && offersRect.top <= 0;
          } else {
            // Fallback to original logic if hero section not found
            shouldBeSticky = rect.top <= 0;
          }

          if (shouldBeSticky !== isSticky) {
            setIsSticky(shouldBeSticky);
          }
        }
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }, [isSticky]);

    // Update markers when hotels change
    useEffect(() => {
      if (!isLoaded || !mapInstanceRef.current) return;

      const updateMarkers = () => {
        try {
          // Clear existing markers
          markersRef.current.forEach((marker) => {
            marker.setMap(null);
          });
          markersRef.current = [];

          // Clear existing marker clusterer
          if (markerClustererRef.current) {
            markerClustererRef.current.clearMarkers();
            markerClustererRef.current = null;
          }

          // Close and reset InfoWindow
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          // Reset clicked marker reference
          clickedMarkerRef.current = null;

          // If no hotels, just clear and return
          if (!hotels.length) {
            return;
          }

          // Add new markers for hotels with coordinates
          const bounds = new window.google.maps.LatLngBounds();
          let hasValidCoordinates = false;

          // Create fresh InfoWindow for new search
          infoWindowRef.current = new window.google.maps.InfoWindow({
            disableAutoPan: true,
            headerDisabled: true,
          });

          hotels.forEach((hotel) => {
            // Check if hotel has valid coordinates
            if (hotel.latitude && hotel.longitude) {
              const position = {
                lat: parseFloat(hotel.latitude),
                lng: parseFloat(hotel.longitude),
              };

              // Create marker
              const marker = new window.google.maps.Marker({
                map: mapInstanceRef.current,
                position: position,
                title: hotel.name,
                icon: {
                  url: hotel.isPassion
                    ? iconMap[passion].unselected
                    : LocationPinBlack,
                  scaledSize: new window.google.maps.Size(54, 87),
                  anchor: new window.google.maps.Point(27, 87),
                },
              });

              // Store hotel ID on marker for reliable lookup
              marker.hotelId = hotel.id;

              // Create custom InfoWindow content
              const createInfoWindowContent = (hotel) => {
                const rating = hotel.reviews?.rating
                  ? parseFloat(hotel.reviews.rating).toFixed(1)
                  : "N/A";
                const reviewCount = hotel.reviews?.count || 0;
                const priceValue =
                  hotel.rate?.lowestAverageNightlyRate?.value ||
                  hotel.rate?.firstNightLowestStay?.value;
                const price = priceValue
                  ? `$${(priceValue / Math.pow(10, hotel.rate.firstNightLowestStay?.valueDecimalPoint || 2)).toFixed(0)}`
                  : "Price not available";
                const description =
                  hotel.description || "No description available";

                return `
                <div class="custom-info-window">
                  <div class="info-window-header">
                    <h3 class="hotel-name">${hotel.name}</h3>
                    <div class="hotel-rating">
                      <span class="rating-stars">★</span>
                      <span class="rating-value">${rating}</span>
                      <span class="review-count">(${reviewCount} reviews)</span>
                    </div>
                  </div>
                  <div class="info-window-content">
                    <p class="hotel-description">${description.length > 100 ? description.substring(0, 100) + "..." : description}</p>
                    <div class="hotel-price">
                      <span class="price-label">From </span>
                      <span class="price-value">${price}</span>
                      <span class="price-period"> per night</span>
                    </div>
                  </div>
                </div>
              `;
              };

              // Store original icon for hover effects
              const originalIcon = {
                url: hotel.isPassion
                  ? iconMap[passion].unselected
                  : LocationPinBlack,
                scaledSize: new window.google.maps.Size(54, 87),
                anchor: new window.google.maps.Point(27, 87),
              };

              const hoverIcon = {
                url: hotel.isPassion
                  ? iconMap[passion].unselected
                  : LocationPinBlack,
                scaledSize: new window.google.maps.Size(60, 96), // Slightly larger on hover
                anchor: new window.google.maps.Point(30, 96),
              };

              // Add hover event listeners for visual feedback
              marker.addListener("mouseover", () => {
                marker.setIcon(hoverIcon);
                // Change cursor to pointer
                mapInstanceRef.current.setOptions({
                  draggableCursor: "pointer",
                });
              });

              marker.addListener("mouseout", () => {
                marker.setIcon(originalIcon);
                // Reset cursor
                mapInstanceRef.current.setOptions({ draggableCursor: "grab" });
              });

              // Add click event listener to center map and keep InfoWindow open on click
              marker.addListener("click", () => {
                // Center the map on the clicked marker
                mapInstanceRef.current.panTo(position);

                // Set this marker as the clicked one
                clickedMarkerRef.current = marker;

                // Open InfoWindow with hotel details and keep it open
                infoWindowRef.current.setContent(
                  createInfoWindowContent(hotel),
                );
                infoWindowRef.current.open(mapInstanceRef.current, marker);

                // Call the callback to scroll to the related card
                if (onMarkerClick) {
                  onMarkerClick(hotel);
                }
              });

              markersRef.current.push(marker);
              bounds.extend(position);
              hasValidCoordinates = true;
            }
          });

          // Fit map to show all markers if we have valid coordinates
          if (hasValidCoordinates) {
            mapInstanceRef.current.fitBounds(bounds);

            // Ensure minimum zoom level
            const listener = window.google.maps.event.addListener(
              mapInstanceRef.current,
              "bounds_changed",
              () => {
                if (mapInstanceRef.current.getZoom() > 10) {
                  mapInstanceRef.current.setZoom(10);
                }
                window.google.maps.event.removeListener(listener);
              },
            );
          }

          // Create new marker clusterer with fresh markers
          if (markersRef.current.length > 0) {
            markerClustererRef.current = new window.MarkerClusterer(
              mapInstanceRef.current,
              markersRef.current,
              {
                gridSize: 30, // Smaller grid size = less aggressive clustering (default is 60)
                minimumClusterSize: 4, // Need at least 4 markers to form a cluster (default is 2)
                maxZoom: 15, // Stop clustering at zoom level 15 and above
              },
            );
          }
        } catch (error) {
          console.error("Error updating map markers:", error);
        }
      };

      updateMarkers();
    }, [hotels, isLoaded, passion]);

    // Cleanup effect
    useEffect(() => {
      return () => {
        // Cleanup markers
        if (markersRef.current) {
          markersRef.current.forEach((marker) => {
            marker.setMap(null);
          });
          markersRef.current = [];
        }

        // Cleanup marker clusterer
        if (markerClustererRef.current) {
          markerClustererRef.current.clearMarkers();
          markerClustererRef.current = null;
        }

        // Cleanup InfoWindow
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }

        // Cleanup state polygon
        if (statePolygonRef.current) {
          statePolygonRef.current.setMap(null);
          statePolygonRef.current = null;
        }

        // Cleanup clickable state polygons
        if (clickableStatePolygonsRef.current) {
          clickableStatePolygonsRef.current.forEach((polygon) => {
            polygon.setMap(null);
          });
          clickableStatePolygonsRef.current = [];
        }
      };
    }, []);

    return (
      <>
        {/* Placeholder to maintain space when map is sticky */}
        {isSticky && (
          <div
            style={{
              width: "49%",
              height: "90vh",
              flexShrink: 0,
            }}
          />
        )}
        <div
          ref={containerRef}
          className={`google-map-container ${className} ${isSticky ? "sticky" : ""}`}
        >
          <div
            ref={mapRef}
            className="google-map"
            style={{ width: "100%", height: "85vh" }}
          />
          {showSearchBtn && searchState && (
            <button
              className="search-location-btn"
              onClick={() => {
                const stateToSearch = hoveredState || searchState;
                if (onSearchByLocation && searchCenter && stateToSearch) {
                  // Set the destination in the search store
                  useSearchStore.getState().setSearchDestination(stateToSearch);

                  onSearchByLocation({
                    center: searchCenter,
                    state: stateToSearch,
                    radius: 50,
                  });
                  // Don't hide the button anymore - it should persist after search
                  // setShowSearchBtn(false);
                  //clearStateHighlight(); // Clear state highlighting after search
                }
              }}
            >
              {`Search in ${hoveredState || searchState}`}
            </button>
          )}
          {(keyLoading || !isLoaded) && (
            <div className="map-loading">
              <div className="map-loading-spinner"></div>
              <span>
                {keyLoading ? "Loading API key..." : "Loading map..."}
              </span>
            </div>
          )}
          {keyError && (
            <div className="map-loading">
              <span style={{ color: "red" }}>
                Failed to load Google Maps API key
              </span>
            </div>
          )}
        </div>
      </>
    );
  },
);

GoogleMap.displayName = "GoogleMap";

export default GoogleMap;
